import {
  THEME_FONT_CHOICES,
  THEME_PRESETS,
  THEME_RADIUS_PRESETS,
  THEME_SPACING_PRESETS,
  contrastRatio,
  type SiteTheme,
  type ThemeFontChoice
} from '@sarnia/shared';
import { editorStore, useEditorSelector } from './editorStore';

type PaletteKey = keyof SiteTheme['palette'];
type FontKey = keyof SiteTheme['fonts'];

const COLOUR_ROWS: Array<{ key: PaletteKey; label: string; hint: string }> = [
  { key: 'background', label: 'Page background', hint: 'Behind everything' },
  { key: 'surface', label: 'Cards & panels', hint: 'Boxes, forms, pricing cards' },
  { key: 'text', label: 'Body text', hint: 'Paragraphs and lists' },
  { key: 'heading', label: 'Headings', hint: 'Titles and big text' },
  { key: 'accent', label: 'Buttons & links', hint: 'Your main brand colour' },
  { key: 'accentText', label: 'Text on buttons', hint: 'Shown on top of the brand colour' },
  { key: 'muted', label: 'Soft text', hint: 'Captions and small print' },
  { key: 'border', label: 'Lines & borders', hint: 'Dividers and outlines' }
];

const FONT_ROWS: Array<{ key: FontKey; label: string; hint: string }> = [
  { key: 'heading', label: 'Headings', hint: 'Titles' },
  { key: 'body', label: 'Body text', hint: 'Paragraphs' },
  { key: 'mono', label: 'Small labels', hint: 'Eyebrows and tags' }
];

const FONT_GROUPS: ThemeFontChoice['group'][] = ['Sans-serif', 'Serif', 'Display & handwriting', 'Monospace', 'System'];

const samePalette = (a: SiteTheme['palette'], b: SiteTheme['palette']): boolean =>
  (Object.keys(a) as PaletteKey[]).every((k) => a[k].toLowerCase() === b[k].toLowerCase());

const sameRadius = (a: SiteTheme['radius'], b: SiteTheme['radius']): boolean => a.sm === b.sm && a.md === b.md && a.lg === b.lg;

function contrastNote(theme: SiteTheme, key: PaletteKey): string | null {
  const p = theme.palette;
  const pairs: Partial<Record<PaletteKey, [string, string, number, string]>> = {
    text: [p.text, p.background, 4.5, 'Hard to read on the background'],
    heading: [p.heading, p.background, 3, 'Hard to read on the background'],
    muted: [p.muted, p.background, 4.5, 'Hard to read on the background'],
    accent: [p.accent, p.background, 3, 'Low contrast with the background'],
    accentText: [p.accentText, p.accent, 4.5, 'Hard to read on buttons']
  };
  const pair = pairs[key];
  if (!pair) return null;
  const ratio = contrastRatio(pair[0], pair[1]);
  return ratio !== null && ratio < pair[2] ? pair[3] : null;
}

export default function ThemePanel() {
  const site = useEditorSelector((state) => state.site);

  if (!site) {
    return <div className="inspector-empty">Theme is not available.</div>;
  }

  const theme = site.theme;
  const update = (patch: Partial<SiteTheme>): void => {
    editorStore.updateTheme(patch);
  };

  const currentSpacing = THEME_SPACING_PRESETS.find(
    (s) => s.spacing.section === theme.spacing.section && s.spacing.container === theme.spacing.container
  );
  const currentRadius = THEME_RADIUS_PRESETS.find((r) => sameRadius(r.radius, theme.radius));

  const fontChoicesFor = (current: string): ThemeFontChoice[] => {
    if (THEME_FONT_CHOICES.some((c) => c.stack === current)) return THEME_FONT_CHOICES;
    const name = (current.split(',')[0] ?? current).replace(/["']/g, '').trim() || 'Custom';
    return [{ label: `${name} (current)`, stack: current, group: 'System', google: null }, ...THEME_FONT_CHOICES];
  };

  return (
    <div className="theme-panel">
      <h3 className="inspector-heading">Theme</h3>

      <section className="theme-section">
        <h4>Start from a look</h4>
        <p className="theme-hint">One click changes colours, fonts and corners. You can tweak anything afterwards.</p>
        <div className="theme-presets" role="list">
          {THEME_PRESETS.map((preset) => {
            const active = samePalette(preset.theme.palette, theme.palette) && preset.theme.fonts.heading === theme.fonts.heading;
            const pl = preset.theme.palette;
            return (
              <button
                key={preset.id}
                type="button"
                role="listitem"
                className={`theme-preset${active ? ' theme-preset--active' : ''}`}
                aria-pressed={active}
                onClick={() =>
                  update({
                    name: preset.theme.name,
                    palette: { ...preset.theme.palette },
                    fonts: { ...preset.theme.fonts },
                    radius: { ...preset.theme.radius }
                  })
                }
              >
                <span className="theme-preset__swatch" style={{ background: pl.background, borderColor: pl.border }}>
                  <span className="theme-preset__aa" style={{ color: pl.heading, fontFamily: preset.theme.fonts.heading }}>
                    Aa
                  </span>
                  <span className="theme-preset__dot" style={{ background: pl.accent }} />
                  <span className="theme-preset__dot theme-preset__dot--small" style={{ background: pl.text }} />
                </span>
                <span className="theme-preset__name">{preset.theme.name}</span>
              </button>
            );
          })}
        </div>
      </section>

      <section className="theme-section">
        <h4>Colours</h4>
        {COLOUR_ROWS.map(({ key, label, hint }) => {
          const note = contrastNote(theme, key);
          return (
            <div className="theme-colour" key={key}>
              <label className="theme-colour__row">
                <span className="theme-colour__text">
                  <span className="theme-colour__label">{label}</span>
                  <span className="theme-colour__hint">{hint}</span>
                </span>
                <span className="theme-colour__control">
                  <code className="theme-colour__hex">{theme.palette[key].toUpperCase()}</code>
                  <input
                    type="color"
                    aria-label={label}
                    value={theme.palette[key]}
                    onChange={(event) => update({ palette: { ...theme.palette, [key]: event.target.value } })}
                  />
                </span>
              </label>
              {note ? (
                <p className="theme-colour__warn" role="note">
                  {note}. Try a lighter or darker shade.
                </p>
              ) : null}
            </div>
          );
        })}
      </section>

      <section className="theme-section">
        <h4>Fonts</h4>
        {FONT_ROWS.map(({ key, label, hint }) => {
          const choices = fontChoicesFor(theme.fonts[key]);
          return (
            <label className="theme-field" key={key}>
              <span className="theme-colour__text">
                <span className="theme-colour__label">{label}</span>
                <span className="theme-colour__hint">{hint}</span>
              </span>
              <select
                value={theme.fonts[key]}
                onChange={(event) => update({ fonts: { ...theme.fonts, [key]: event.target.value } })}
              >
                {FONT_GROUPS.map((group) => {
                  const inGroup = choices.filter((c) => c.group === group);
                  return inGroup.length ? (
                    <optgroup label={group} key={group}>
                      {inGroup.map((c) => (
                        <option value={c.stack} key={c.stack}>
                          {c.label}
                        </option>
                      ))}
                    </optgroup>
                  ) : null;
                })}
              </select>
            </label>
          );
        })}
      </section>

      <section className="theme-section">
        <h4>Corners</h4>
        <div className="theme-segment" role="group" aria-label="Corner style">
          {THEME_RADIUS_PRESETS.map((r) => (
            <button
              key={r.id}
              type="button"
              aria-pressed={currentRadius?.id === r.id}
              className={`theme-segment__btn${currentRadius?.id === r.id ? ' theme-segment__btn--on' : ''}`}
              onClick={() => update({ radius: { ...r.radius } })}
            >
              {r.label}
            </button>
          ))}
        </div>
      </section>

      <section className="theme-section">
        <h4>Spacing</h4>
        <div className="theme-segment" role="group" aria-label="Spacing">
          {THEME_SPACING_PRESETS.map((s) => (
            <button
              key={s.id}
              type="button"
              aria-pressed={currentSpacing?.id === s.id}
              className={`theme-segment__btn${currentSpacing?.id === s.id ? ' theme-segment__btn--on' : ''}`}
              onClick={() => update({ spacing: { ...s.spacing } })}
            >
              {s.label}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
